// The candidate's app (PRD section 5).
//
// It lives OUTSIDE the product's Layout on purpose: no nav, no toolkit, no
// programs, no Elliott outside the assessment, no people and no settings
// (C-9). The server refuses everything else anyway, through an endpoint
// allow-list (PRD 6.1), and this route is the visible half of that: there is
// nothing on screen to try.
//
// The step comes from the server, never from local state. That is what makes
// resuming free (C-6) and what stops a reload skipping a step that has legal
// weight: a candidate cannot get past the notice by going back (C-2, L-1).

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../components/ui/DashboardSkeletons';
import { hiringApi, hiringMessage, isCandidate } from '../../api/hiring';
import { useCandidateStep, useHiringMe } from '../../queries/useHiring';
import { BODY, DANGER, FONT, TEXT, TEXT_2 } from '../new-designs/tabs/capability-setup/designSystem';
import {
  BlockedStep,
  ClosedStep,
  DoneStep,
  NoticeStep,
  OnboardingStep,
  ResidenceStep,
  WelcomeStep
} from './candidateParts';
import CandidateAssessment from './CandidateAssessment';

/** 10 MB, matching the server. Checked here too so a big file fails before the upload. */
const MAX_CV_BYTES = 10 * 1024 * 1024;

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      // The data URL's prefix is not part of the file.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.readAsDataURL(file);
  });

const CandidateApp: React.FC = () => {
  const me = useHiringMe();
  const step = useCandidateStep();
  const [error, setError] = useState<string | null>(null);

  const run = (work: () => Promise<unknown>) => {
    setError(null);
    step.mutate(work, { onError: (err) => setError(hiringMessage(err)) });
  };

  if (me.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0F0F0F', p: '40px' }}>
        <Box sx={{ maxWidth: 680, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
          <Bone width="50%" height={24} />
          <Bone height={240} />
        </Box>
      </Box>
    );
  }

  // Someone who is not a candidate lands here only by typing the URL. The
  // server tells us so with `{ candidate: false }` and a 200, not a refusal:
  // there is nothing to show them, and that is not an error on their part.
  if (me.isError || !isCandidate(me.data)) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0F0F0F', p: '40px' }}>
        <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
            There is no assessment here
          </Typography>
          <Typography sx={{ ...BODY, color: TEXT_2 }}>
            This account isn&apos;t a candidate for a hiring round. If you were sent an invitation, please open the link
            in that email.
          </Typography>
        </Box>
      </Box>
    );
  }

  const { data } = me;
  const busy = step.isPending;
  const refresh = () => me.refetch();

  if (data.step === 'blocked') return <BlockedStep me={data} />;
  if (data.step === 'closed') return <ClosedStep me={data} />;
  if (data.step === 'done') return <DoneStep me={data} />;
  if (data.step === 'residence') {
    return (
      <ResidenceStep
        me={data}
        busy={busy}
        error={error}
        onSubmit={(input) => run(() => hiringApi.setResidence(input))}
      />
    );
  }
  if (data.step === 'notice') {
    return (
      <NoticeStep
        me={data}
        busy={busy}
        error={error}
        onAcknowledge={(version) => run(() => hiringApi.acknowledgeNotice(version))}
      />
    );
  }
  if (data.step === 'onboarding') {
    return (
      <OnboardingStep
        me={data}
        busy={busy}
        error={error}
        onSave={(input) => run(() => hiringApi.setOnboarding(input))}
        onUploadCv={async (file) => {
          if (file.size > MAX_CV_BYTES) {
            setError('That file is too large: the limit is 10 MB.');
            return;
          }
          try {
            const fileBase64 = await readAsBase64(file);
            run(() =>
              hiringApi.uploadCv({
                fileBase64,
                fileName: file.name,
                // Some browsers give no type for a .docx; the server checks it
                // again and refuses anything that is not a PDF or a Word file.
                contentType:
                  file.type ||
                  (file.name.toLowerCase().endsWith('.pdf')
                    ? 'application/pdf'
                    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
              })
            );
          } catch (err) {
            setError(hiringMessage(err, 'That file could not be read'));
          }
        }}
      />
    );
  }
  if (data.step === 'welcome') {
    return <WelcomeStep me={data} busy={busy} error={error} onBegin={() => run(() => hiringApi.start())} />;
  }
  return <CandidateAssessment me={data} onChanged={refresh} />;
};

export default CandidateApp;
